import React from 'react';
import { Box, Typography, Chip, List, ListItem } from '@mui/material';
import moment from 'moment';
import { MedicationEvent } from '../types';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faClock,
  faCircleUser,
  faCalendar,
} from '@fortawesome/pro-light-svg-icons';
import {
  getStatusBackgroundColor,
  getStatusIcon,
  getStatusLabel,
  mapApiStatusToEnum,
  Status,
} from '../utils/statusUtils';
import { ReactComponent as CapsuleIcon } from '../../../../../../images/icons/capsule_inherit.svg';
import { dimGrey, lightBlack, orange } from '../../../../../../mainTheme';

interface Props {
  event: MedicationEvent;
  switchToMedicationTab?: () => void;
}

const ScheduleEventDetails: React.FC<Props> = ({
  event,
  switchToMedicationTab,
}) => (
  <>
    {/* Status Chip */}
    <Chip
      icon={getStatusIcon(event.status) ?? undefined}
      label={getStatusLabel(event.status)}
      size="small"
      sx={{
        backgroundColor: getStatusBackgroundColor(event.status),
        color: lightBlack,
        mb: 1,
        fontWeight: 500,
        fontSize: 12,
        px: 1.5,
        height: 30,
        gap: '4px',
        mt: '16px',
      }}
    />

    {/* Event Details */}
    <Box sx={{ width: '100%', mt: '16px' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <FontAwesomeIcon
          icon={faClock}
          style={{ fontSize: 14, color: '#9999A3' }}
        />
        <Typography
          sx={{
            color: dimGrey,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '16px',
          }}
        >
          {moment
            .tz(event.desired_datetime, event.timezone || '')
            .format('h:mm A z')}
        </Typography>
        <FontAwesomeIcon
          icon={faCalendar}
          style={{ fontSize: 14, color: '#9999A3' }}
        />
        <Typography
          sx={{
            color: dimGrey,
            fontSize: '16px',
            fontWeight: 500,
            lineHeight: '16px',
          }}
        >
          {moment.tz(event.start, 'America/Chicago').format('MMM Do, YYYY')}
        </Typography>
      </Box>
      {event.caregiver && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FontAwesomeIcon
            icon={faCircleUser}
            style={{ fontSize: 14, color: '#9999A3' }}
          />
          <Typography
            sx={{
              color: dimGrey,
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
            component="span"
          >
            Caregiver assist:{' '}
            <Box
              component="span"
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '16px',
                color: lightBlack,
              }}
            >
              {event.caregiver}
            </Box>
          </Typography>
        </Box>
      )}
      {event.owner && (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
          <FontAwesomeIcon
            icon={faCircleUser}
            style={{ fontSize: 14, color: '#9999A3' }}
          />
          <Typography
            sx={{
              color: dimGrey,
              fontSize: '16px',
              fontWeight: 500,
              lineHeight: '16px',
            }}
            component="span"
          >
            Owner:{' '}
            <Box
              component="span"
              sx={{
                fontSize: '16px',
                fontWeight: 700,
                lineHeight: '16px',
                color: lightBlack,
              }}
            >
              {event.owner}
            </Box>
          </Typography>
        </Box>
      )}
    </Box>

    {/* Medications */}
    <Typography
      sx={{
        fontSize: '16px',
        fontWeight: 700,
        lineHeight: '20px',
        color: lightBlack,
        letterSpacing: '0.2px',
        mt: '24px',
      }}
    >
      Medication{' '}
      <Box component="span" sx={{ fontWeight: 500, color: '#9999A3' }}>
        ({event.medications.length})
      </Box>
    </Typography>
    <List
      dense
      sx={{ p: 0, width: '100%', maxHeight: '132px', overflow: 'auto' }}
    >
      {event.medications.map((medication, idx) => (
        <ListItem key={`${medication.id}-${idx}`} sx={{ px: 0, py: 0.75 }}>
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              width: '100%',
              justifyContent: 'space-between',
              gap: 1,
            }}
          >
            <Box sx={{ flex: 1 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Chip
                  label={medication.name}
                  sx={{
                    backgroundColor: getStatusBackgroundColor(
                      medication.status,
                    ),
                    color: lightBlack,
                    fontWeight: 500,
                    fontSize: 12,
                    px: 1.5,
                    height: 30,
                  }}
                  onClick={switchToMedicationTab}
                />
                {mapApiStatusToEnum(medication.status) === Status.MISSED && (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    {getStatusIcon(medication.status)}
                    <Typography
                      sx={{
                        fontSize: '14px',
                        lineHeight: '16px',
                        color: orange,
                        fontWeight: 500,
                      }}
                    >
                      {getStatusLabel(medication.status)}
                    </Typography>
                  </Box>
                )}
              </Box>
              <Box display="flex" alignItems="center" sx={{ m: 1 }}>
                <CapsuleIcon
                  width={16}
                  height={16}
                  style={{ marginRight: 8, display: 'inline-block' }}
                />
                <Typography
                  sx={{
                    fontSize: '14px',
                    lineHeight: '16px',
                    color: dimGrey,
                    fontWeight: 500,
                  }}
                  component="span"
                >
                  {medication.dosage} QTY{' '}
                  <Box
                    component="span"
                    sx={{ color: lightBlack, fontWeight: 700 }}
                  >
                    {medication.quantity}
                  </Box>
                </Typography>
              </Box>
            </Box>
          </Box>
        </ListItem>
      ))}
    </List>
  </>
);

export default ScheduleEventDetails;
